import { useCallback, useMemo, useState } from 'react';
import type {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import { useGridStateStore } from '@/stores/grid-state-store';

interface GridStateDefaults {
  paginationModel?: GridPaginationModel;
  sortModel?: GridSortModel;
  filterModel?: GridFilterModel;
}

const DEFAULT_PAGINATION: GridPaginationModel = { page: 0, pageSize: 100 };
const DEFAULT_SORT: GridSortModel = [];
const DEFAULT_FILTER: GridFilterModel = { items: [], quickFilterValues: [] };

export function useGridState(pageId: string, defaults: GridStateDefaults = {}) {
  // Capture the defaults from the first render so callbacks stay stable.
  const [initialDefaults] = useState(defaults);

  const pageState = useGridStateStore(
    useCallback((s) => s.pages[pageId], [pageId]),
  );

  const paginationModel: GridPaginationModel =
    pageState?.paginationModel ?? initialDefaults.paginationModel ?? DEFAULT_PAGINATION;

  const setPaginationModel = useCallback(
    (modelOrUpdater: GridPaginationModel | ((prev: GridPaginationModel) => GridPaginationModel)) => {
      const store = useGridStateStore.getState();
      const current = store.pages[pageId]?.paginationModel
        ?? initialDefaults.paginationModel ?? DEFAULT_PAGINATION;
      const next = typeof modelOrUpdater === 'function' ? modelOrUpdater(current) : modelOrUpdater;
      if (next.page === current.page && next.pageSize === current.pageSize) return;
      store.setPageState(pageId, { paginationModel: next });
    },
    [pageId, initialDefaults],
  );

  const sortModel: GridSortModel =
    pageState?.sortModel ?? initialDefaults.sortModel ?? DEFAULT_SORT;

  const setSortModel = useCallback(
    (model: GridSortModel) => useGridStateStore.getState().setPageState(pageId, { sortModel: model }),
    [pageId],
  );

  const filterModel: GridFilterModel =
    pageState?.filterModel ?? initialDefaults.filterModel ?? DEFAULT_FILTER;

  const setFilterModel = useCallback(
    (model: GridFilterModel) => useGridStateStore.getState().setPageState(pageId, { filterModel: model }),
    [pageId],
  );

  const custom = pageState?.custom;

  const getCustom = useCallback(
    <T,>(key: string, fallback: T): T =>
      (custom?.[key] as T) ?? fallback,
    [custom],
  );

  const setCustom = useCallback(
    (key: string, value: unknown) => useGridStateStore.getState().setCustom(pageId, key, value),
    [pageId],
  );

  const resetPage = useCallback(
    () => useGridStateStore.getState().clearPage(pageId),
    [pageId],
  );

  return useMemo(() => ({
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    getCustom,
    setCustom,
    resetPage,
  }), [
    paginationModel, setPaginationModel,
    sortModel, setSortModel,
    filterModel, setFilterModel,
    getCustom, setCustom, resetPage,
  ]);
}
